import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx, sizeClass, type Hue, type Size } from './shared';

export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'fancy';

export interface ButtonStyleOptions {
  /** Palette hue. Default cyan. */
  hue?: Hue;
  /** solid (default), outline, ghost, or fancy (the SWTOR SVG frame). */
  variant?: ButtonVariant;
  size?: Size;
  /** Full width. */
  block?: boolean;
  /** Square, for an icon on its own (give it an aria-label). */
  icon?: boolean;
  pill?: boolean;
  /** Fancy only: the shorter frame shape. */
  compact?: boolean;
  /** Pressed or selected. */
  active?: boolean;
  loading?: boolean;
}

/** The class list for a button, for links styled as buttons: <a className={buttonClasses({ hue: 'red' })}>. */
export function buttonClasses(
  { hue, variant = 'solid', size, block, icon, pill, compact, active, loading }: ButtonStyleOptions,
  className?: string,
) {
  return cx(
    'btn',
    variant !== 'solid' && `btn--${variant}`,
    hue && `btn--${hue}`,
    sizeClass('btn', size),
    block && 'btn--block',
    icon && 'btn--icon',
    pill && 'btn--pill',
    compact && 'btn--compact',
    active && 'is-active',
    loading && 'is-loading',
    className,
  );
}

export interface ButtonProps extends ButtonStyleOptions, ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
}

export function Button({
  hue,
  variant,
  size,
  block,
  icon,
  pill,
  compact,
  active,
  loading,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClasses({ hue, variant, size, block, icon, pill, compact, active, loading }, className)}
      aria-busy={loading || undefined}
      aria-pressed={active ?? undefined}
      {...rest}
    >
      {children}
      {loading && <span className="btn__spinner" aria-hidden="true" />}
    </button>
  );
}
